import React from 'react';
import { Modal } from 'syngenta-digital-cropwise-react-ui-kit';

import ErrorCircleOutlineIcon from 'assets/error-circle-outline.svg?react';
import { LayoutStrings } from '../Constants/layoutStrings';

// Error type constants - export for use in other files
export const WEATHER_ERROR_TYPES = {
  CALC_CENTROID_ERROR: 'calcCentroidError',
  WEATHER_API_ERROR: 'weatherAPIError'
} as const;

export type WeatherErrorType = typeof WEATHER_ERROR_TYPES[keyof typeof WEATHER_ERROR_TYPES];

interface ShowWeatherErrorModalProps {
  onSaveWithoutWeather: () => void;
  onContinueEditing: () => void;
  errorType?: WeatherErrorType;
}

const showWeatherErrorModal = ({
  onSaveWithoutWeather,
  onContinueEditing,
  errorType = WEATHER_ERROR_TYPES.CALC_CENTROID_ERROR
}: ShowWeatherErrorModalProps) => {
  const title = 'Unable to download weather data';

  const calcCentroidErrorContent = (
    <div style={{ marginLeft: 36 }}>
      <p style={{ margin: 0 }}>
        We&apos;re having difficulty calculating the center point between your
        selected fields. To learn more about how we provide automatic weather
        data,{' '}
        <a
          href="https://www.syngentadigital.com/us-en#support"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0092E4', textDecoration: 'underline' }}
        >
          visit our support center
        </a>
        {'. '}
      </p>
      <br />
      <p style={{ margin: 0 }}>
        You can add weather information manually, or save without weather.
      </p>
    </div>
  );

  const weatherAPIErrorContent = (
    <div style={{ marginLeft: 36 }}>
      <p style={{ margin: 0 }}>
        An error prevented us from getting your weather data at this time. You
        can add weather information manually, or try again later.
      </p>
    </div>
  );

  const content =
    errorType === WEATHER_ERROR_TYPES.CALC_CENTROID_ERROR
      ? calcCentroidErrorContent
      : weatherAPIErrorContent;

  Modal.confirm({
    icon: (
      <span className="flex items-center" style={{ gap: 12 }}>
        <span className="flex items-center">
          <ErrorCircleOutlineIcon
            width={24}
            height={24}
            style={{ color: '#CF3537' }}
          />
        </span>
        <span
          className="font-semibold"
          style={{ fontSize: 18, color: '#14151C' }}
        >
          {title}
        </span>
      </span>
    ),
    content,
    okText: 'Save Without Weather',
    cancelText: LayoutStrings.modals.buttonText.continueEditing,
    centered: true,
    closable: false,
    maskClosable: false,
    transitionName: '',
    maskTransitionName: '',
    width: 493,
    bodyStyle: {
      padding: 16
    },
    okButtonProps: {
      style: {
        background: '#CF3537',
        color: 'var(--Neutral-neutral-00, #FFF)',
        marginLeft: 8,
        padding: '10px 16px',
        borderRadius: 4,
        fontSize: 14,
        fontWeight: 400
      }
    },
    cancelButtonProps: {
      style: {
        background: 'var(--Neutral-neutral-00, #FFF)',
        color: 'var(--Neutral-neutral-100, #14151C)',
        marginRight: 0,
        padding: '10px 16px',
        borderRadius: 4,
        fontSize: 14,
        fontWeight: 400,
        border: '1px solid var(--Neutral-neutral-30, #C2C7D0)'
      }
    },
    onOk: onSaveWithoutWeather,
    onCancel: onContinueEditing
  });
};

export default showWeatherErrorModal;
